/**
 * useTournamentPuzzles — host-only custom puzzle set for a scheduled tournament.
 *
 *   GET  /fun_challenges/v1/royale_tournaments/<id>/puzzles/         status
 *   POST /fun_challenges/v1/royale_tournaments/<id>/puzzles/upload/  upload .pgn
 *   POST /fun_challenges/v1/royale_tournaments/<id>/puzzles/clear/   remove set
 *
 * These are the only browser-facing WRITES in the tournament flow. Every other
 * lifecycle write goes over the socket (useRoyaleSocket), but a multipart file
 * upload cannot ride a socket message and the service-only create endpoint is not
 * reachable from a browser — hence a REST endpoint gated by staff +
 * host-ownership.
 *
 * Upload is all-or-nothing by design: if any game in the .pgn fails to parse the
 * server saves NOTHING and returns a per-game error list, so a host can never end
 * up with a silently short set.
 */

import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';

/** One unparseable game, by 1-based position in the uploaded file. */
export interface TournamentPuzzleError {
  game: number;
  error: string;
}

export interface TournamentPuzzleStatus {
  ok?: boolean;
  tournament_id?: number;
  /** Whether the tournament is FLAGGED for custom puzzles. Can be true with
   *  puzzle_count 0 — the host ticked the box but never uploaded, in which case
   *  the race falls back to normal band puzzles. */
  custom_puzzles: boolean;
  puzzle_count: number;
  puzzle_book_name: string | null;
  puzzle_book_id: string | null;
  /** Detected shape split, so a mis-shaped file is visible before the race. */
  no_setup_solver_first: number;
  no_setup_opponent_first: number;
}

export interface TournamentPuzzleUploadResult {
  ok: boolean;
  tournament_id: number;
  count: number;
  puzzle_book_id: string;
  puzzle_book_name: string | null;
  no_setup_solver_first: number;
  no_setup_opponent_first: number;
  forced_no_setup: boolean;
}

export const tournamentPuzzlesKey = (id: string | number) =>
  ['royale_tournament_puzzles', String(id)] as const;

/** Host-only puzzle-set status. `enabled: false` for non-hosts (the endpoint
 *  403s for anyone who isn't the creator, and we don't want that noise). */
export function useTournamentPuzzleStatus(
  tournamentId: number | string | null | undefined,
  opts?: {enabled?: boolean},
) {
  const idStr = tournamentId != null ? String(tournamentId) : '';
  return useQuery<TournamentPuzzleStatus, unknown>(
    tournamentPuzzlesKey(idStr),
    async () => {
      const res = await ChessRestClient.get(
        `fun_challenges/v1/royale_tournaments/${idStr}/puzzles/`,
      );
      return res.data as TournamentPuzzleStatus;
    },
    {
      enabled: !!idStr && opts?.enabled !== false,
      staleTime: 5_000,
      retry: false, // a 403 for a non-host must not be retried
    },
  );
}

/**
 * Upload a .pgn as the tournament's puzzle set (replaces any existing set).
 *
 * Rejects with an object carrying `{message, errors}` so the caller can render
 * the per-game list verbatim — the host needs to know WHICH games are bad, not
 * just that something failed.
 */
export function useUploadTournamentPuzzles(tournamentId: number | string) {
  const qc = useQueryClient();
  return useMutation<
    TournamentPuzzleUploadResult,
    {message: string; errors: TournamentPuzzleError[]},
    {file: File; bookName?: string; forceNoSetup?: boolean}
  >(
    async ({file, bookName, forceNoSetup}) => {
      const form = new FormData();
      form.append('file', file);
      if (bookName) {
        form.append('book_name', bookName);
      }
      if (forceNoSetup) {
        form.append('force_no_setup', '1');
      }
      try {
        const res = await ChessRestClient.post(
          `fun_challenges/v1/royale_tournaments/${tournamentId}/puzzles/upload/`,
          form,
          // Let the browser set the multipart boundary; a hand-set
          // Content-Type without one makes Django see zero files.
          {headers: {'Content-Type': 'multipart/form-data'}},
        );
        return res.data as TournamentPuzzleUploadResult;
      } catch (e: any) {
        const data = e?.response?.data || {};
        throw {
          message:
            data.error ||
            e?.message ||
            'Upload failed. Please check the file and try again.',
          errors: Array.isArray(data.errors) ? data.errors : [],
        };
      }
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(tournamentPuzzlesKey(tournamentId));
        // The tournament summary carries custom_puzzles, which upload flips on.
        qc.invalidateQueries(['royale_tournaments']);
      },
    },
  );
}

/** Remove the custom set and go back to normal band puzzles. */
export function useClearTournamentPuzzles(tournamentId: number | string) {
  const qc = useQueryClient();
  return useMutation<{ok: boolean; removed: number}, unknown, void>(
    async () => {
      const res = await ChessRestClient.post(
        `fun_challenges/v1/royale_tournaments/${tournamentId}/puzzles/clear/`,
        {},
      );
      return res.data as {ok: boolean; removed: number};
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(tournamentPuzzlesKey(tournamentId));
        qc.invalidateQueries(['royale_tournaments']);
      },
    },
  );
}
